import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '@/lib/utils';
import { 
  Users, 
  CurrencyCircleDollar, 
  CheckCircle, 
  Wallet,
  TrendUp,
  Calendar
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/dashboard`, {
        withCredentials: true
      });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A227]"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Customers',
      value: stats?.total_customers || 0,
      icon: Users,
      color: '#10B981',
      testId: 'stat-customers'
    },
    {
      title: 'Active Loans',
      value: stats?.active_loans || 0,
      icon: CurrencyCircleDollar,
      color: '#F59E0B',
      testId: 'stat-active-loans'
    },
    {
      title: 'Closed Loans',
      value: stats?.closed_loans || 0,
      icon: CheckCircle,
      color: '#3B82F6',
      testId: 'stat-closed-loans'
    },
    {
      title: 'Total Disbursed',
      value: formatCurrency(stats?.total_disbursed || 0),
      icon: Wallet,
      color: '#8B5CF6',
      testId: 'stat-disbursed',
      mono: true
    },
    {
      title: 'Total Outstanding',
      value: formatCurrency(stats?.total_outstanding || 0),
      icon: TrendUp,
      color: '#EF4444',
      testId: 'stat-outstanding',
      mono: true
    },
    {
      title: "Today's Collection",
      value: formatCurrency(stats?.today_collection || 0),
      icon: Calendar,
      color: '#C9A227',
      testId: 'stat-today-collection',
      mono: true
    }
  ];

  return (
    <div data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-[#6B7280] mt-2">Overview of your gold & silver loan operations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.title}
              className="bg-white rounded-lg border border-[#E5E7EB] p-6 hover:shadow-md transition-shadow"
              data-testid={stat.testId}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-[#6B7280] mb-2">{stat.title}</p>
                  <p 
                    className={`text-3xl font-bold text-[#1A1A1A] ${stat.mono ? 'font-mono-num' : ''}`}
                    style={stat.mono ? { fontFamily: 'JetBrains Mono, monospace' } : { fontFamily: 'Work Sans, sans-serif' }}
                  >
                    {stat.value}
                  </p>
                </div>
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}15` }}
                >
                  <Icon size={24} weight="duotone" style={{ color: stat.color }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Quick Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB]">
              <span className="text-[#6B7280]">Total Loans</span>
              <span className="font-semibold text-[#1A1A1A]">
                {(stats?.active_loans || 0) + (stats?.closed_loans || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB]">
              <span className="text-[#6B7280]">Collection Rate</span>
              <span className="font-semibold text-[#10B981]">
                {stats?.total_disbursed > 0 
                  ? `${(((stats.total_disbursed - stats.total_outstanding) / stats.total_disbursed) * 100).toFixed(1)}%`
                  : '0%'
                }
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Avg Loan Size</span>
              <span className="font-semibold text-[#1A1A1A] font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.active_loans > 0 
                  ? formatCurrency(stats.total_disbursed / stats.active_loans)
                  : formatCurrency(0)
                }
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Performance
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#6B7280]">Active Loans</span>
                <span className="text-[#1A1A1A] font-semibold">
                  {stats?.active_loans || 0} / {(stats?.active_loans || 0) + (stats?.closed_loans || 0)}
                </span>
              </div>
              <div className="w-full bg-[#F9F9FB] rounded-full h-2">
                <div 
                  className="bg-[#F59E0B] h-2 rounded-full transition-all"
                  style={{ 
                    width: `${((stats?.active_loans || 0) / Math.max((stats?.active_loans || 0) + (stats?.closed_loans || 0), 1)) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#6B7280]">Outstanding Amount</span>
                <span className="text-[#1A1A1A] font-semibold font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatCurrency(stats?.total_outstanding || 0)}
                </span>
              </div>
              <div className="w-full bg-[#F9F9FB] rounded-full h-2">
                <div 
                  className="bg-[#EF4444] h-2 rounded-full transition-all"
                  style={{ 
                    width: `${((stats?.total_outstanding || 0) / Math.max(stats?.total_disbursed || 1, 1)) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
